import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { useAuth } from "../../../contexts/AuthContext";
import { useConvexProjects, useConvexFiles } from "../../../services/convexService";
import { toast } from "sonner";
import { Upload, FileText, Image as ImageIcon, X, Home, LogOut, Download } from "lucide-react";
import { Id } from "../../../../backend/_generated/dataModel";
import { prepareFileForUpload } from "../../../utils/fileUtils";
import { api } from "../../../../backend/_generated/api";
import { useConvex } from "convex/react";
import { useLanguage } from "../../../contexts/LanguageContext";

const EditProject = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, language } = useLanguage();
  const { getProjectById, updateProject } = useConvexProjects();
  const { storeFileMetadata, deleteFileMetadata, getFilesByProject } = useConvexFiles();
  const convex = useConvex();
  const uploadFileToStorageAction = api.fileUpload.uploadFileToStorage;

  // Get the specific project
  const project = getProjectById(projectId as Id<"projects">);
  const projectFiles = getFilesByProject(projectId as Id<"projects">);

  // Check if the user is the owner of the project
  const isProjectOwner = user && project && user.id === project.ownerId;
  const isAdmin = user?.role === "admin";

  // If user is not the project owner and not an admin, redirect
  useEffect(() => {
    if (project && user && !isProjectOwner && !isAdmin) {
      toast.error("You don't have permission to edit this project");
      navigate("/dashboard/project-holder"); // Redirect project holders, or to appropriate dashboard
    }
  }, [project, user, isProjectOwner, isAdmin, navigate]);

  // If user is not logged in or doesn't have permission, show error
  if (project && user && !isProjectOwner && !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
          <p className="text-muted-foreground mb-6">You don't have permission to edit this project.</p>
          <Button asChild>
            <Link to={isAdmin ? "/admin" : "/dashboard/project-holder"}>Back to Dashboard</Link>
          </Button>
        </div>
      </div>
    );
  }

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    fundingGoal: "",
    minInvestment: "",
    expectedROI: "",
    projectPurpose: "",
    location: "",
    // Category-specific fields
    developmentStage: "",
    problemStatement: "",
    solutionDescription: "",
    technologyDetails: "",
    ipStatus: "",
    marketPotential: "",
    monetizationOptions: "",
    assetsIncluded: [], // Assets included as checkmarks
    financialOverview: "" // Financial overview for admin only
  });
  const [images, setImages] = useState<File[]>([]);
  const [documents, setDocuments] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (project) {
      setFormData(prev => ({
        ...prev,
        title: project.title || "",
        description: project.description || "",
        category: project.category || "",
        fundingGoal: project.fundingGoal?.toString() || "",
        minInvestment: project.minInvestment?.toString() || "",
        expectedROI: project.expectedROI?.toString() || "",
        projectPurpose: project.projectPurpose || "",
        location: project.location || "",
        // Category-specific fields
        developmentStage: project.developmentStage || "",
        problemStatement: project.problemStatement || "",
        solutionDescription: project.solutionDescription || "",
        technologyDetails: project.technologyDetails || "",
        ipStatus: project.ipStatus || "",
        marketPotential: project.marketPotential || "",
        monetizationOptions: project.monetizationOptions || "",
        assetsIncluded: Array.isArray(project.assetsIncluded) ? project.assetsIncluded : [],
        financialOverview: (project as any).financialOverview || "",
      }));
    }
  }, [project]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSelectChange = (name: string, value: string) => {
    // Only update if the field exists in our form data
    if (name === 'riskLevel') {
      // Skip riskLevel as it's been removed
      return;
    }
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setImages(prev => [...prev, ...files]);
    }
  };

  const handleDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setDocuments(prev => [...prev, ...files]);
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeDocument = (index: number) => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  // Function to delete an existing image from the project
  const deleteExistingImage = async (imageIndex: number) => {
    if (!project || !projectFiles) return;

    // Get the image URL to delete
    const imageToDelete = project.images?.[imageIndex];
    if (!imageToDelete) return;

    try {
      // Find the corresponding file record in the files table
      const fileRecord = projectFiles.find(file => file.url === imageToDelete);
      if (fileRecord) {
        // Delete the file metadata and storage
        await deleteFileMetadata({ fileId: fileRecord._id });
      }

      // Update the project to remove the image URL
      const updatedImages = project.images?.filter((_, i) => i !== imageIndex) || [];

      // Update the project record to remove the image
      await updateProject({
        projectId: projectId as Id<"projects">,
        title: project.title,
        description: project.description,
        category: project.category,
        fundingGoal: project.fundingGoal,
        minInvestment: project.minInvestment,
        expectedROI: project.expectedROI,
        images: updatedImages.length > 0 ? updatedImages : undefined,
        documents: project.documents,
      });

      toast.success("Image deleted successfully!");

      // Instead of reloading, we'll just update the local state
      // This will trigger a re-render with the updated project data
      // The project data will be refetched by the useQuery hook automatically
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Failed to delete image. Please try again.");
    }
  };

  // Function to delete an existing document from the project
  const deleteExistingDocument = async (documentIndex: number) => {
    if (!project || !projectFiles) return;

    // Get the document URL to delete
    const documentToDelete = project.documents?.[documentIndex];
    if (!documentToDelete) return;

    try {
      // Find the corresponding file record in the files table
      const fileRecord = projectFiles.find(file => file.url === documentToDelete);
      if (fileRecord) {
        // Delete the file metadata and storage
        await deleteFileMetadata({ fileId: fileRecord._id });
      }

      // Update the project to remove the document URL
      const updatedDocuments = project.documents?.filter((_, i) => i !== documentIndex) || [];

      // Update the project record to remove the document
      await updateProject({
        projectId: projectId as Id<"projects">,
        title: project.title,
        description: project.description,
        category: project.category,
        fundingGoal: project.fundingGoal,
        minInvestment: project.minInvestment,
        expectedROI: project.expectedROI,
        images: project.images,
        documents: updatedDocuments.length > 0 ? updatedDocuments : undefined,
      });

      toast.success("Document deleted successfully!");

      // The project data will be refetched by the useQuery hook automatically
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error("Failed to delete document. Please try again.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast.error("You must be logged in to edit a project");
      return;
    }

    if (!isProjectOwner && !isAdmin) {
      toast.error("You don't have permission to edit this project");
      return;
    }

    if (!formData.title || !formData.description || !formData.category ||
      !formData.fundingGoal || !formData.minInvestment || !formData.expectedROI) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);

    try {
      // Upload new images to Convex storage if any
      const newImageUrls: string[] = [];
      for (const image of images) {
        const convexFile = await prepareFileForUpload(image);
        const uploadResult = await convex.action(uploadFileToStorageAction, { file: convexFile });
        const url = await uploadResult.url;

        // Store file metadata in the database
        await storeFileMetadata({
          filename: uploadResult.filename,
          contentType: uploadResult.type,
          size: uploadResult.size,
          url: url,
          storageId: uploadResult.storageId,
          uploadedBy: user.id,
          projectId: projectId as Id<"projects">,
          fileType: "image",
        });

        newImageUrls.push(url);
      }

      // Upload new documents to Convex storage if any
      const newDocumentUrls: string[] = [];
      for (const document of documents) {
        const convexFile = await prepareFileForUpload(document);
        const uploadResult = await convex.action(uploadFileToStorageAction, { file: convexFile });
        const url = await uploadResult.url;

        // Store file metadata in the database
        await storeFileMetadata({
          filename: uploadResult.filename,
          contentType: uploadResult.type,
          size: uploadResult.size,
          url: url,
          storageId: uploadResult.storageId,
          uploadedBy: user.id,
          projectId: projectId as Id<"projects">,
          fileType: "document",
        });

        newDocumentUrls.push(url);
      }

      // Use the current project images/documents (after any deletions)
      // The delete functions already update the project data, so we can use the current state
      const allImageUrls = [...(project?.images || []), ...newImageUrls];
      const allDocumentUrls = [...(project?.documents || []), ...newDocumentUrls];

      // Prepare update data object
      const updateData: any = {
        projectId: projectId as Id<"projects">,
        title: formData.title,
        description: formData.description,
        category: formData.category,
        fundingGoal: parseFloat(formData.fundingGoal),
        minInvestment: parseFloat(formData.minInvestment),
        expectedROI: parseFloat(formData.expectedROI),
        images: allImageUrls.length > 0 ? allImageUrls : undefined,
        documents: allDocumentUrls.length > 0 ? allDocumentUrls : undefined,
      };

      // Only add projectPurpose if it's defined and not empty
      if (formData.projectPurpose) {
        updateData.projectPurpose = formData.projectPurpose;
      }

      // Add category-specific fields
      if (formData.developmentStage) updateData.developmentStage = formData.developmentStage;
      if (formData.problemStatement) updateData.problemStatement = formData.problemStatement;
      if (formData.solutionDescription) updateData.solutionDescription = formData.solutionDescription;
      if (formData.technologyDetails) updateData.technologyDetails = formData.technologyDetails;
      if (formData.ipStatus) updateData.ipStatus = formData.ipStatus;
      if (formData.marketPotential) updateData.marketPotential = formData.marketPotential;
      if (formData.monetizationOptions) updateData.monetizationOptions = formData.monetizationOptions;
      if (formData.assetsIncluded && formData.assetsIncluded.length > 0) updateData.assetsIncluded = formData.assetsIncluded;
      if (formData.location) updateData.location = formData.location;
      if (formData.financialOverview) updateData.financialOverview = formData.financialOverview;

      const result = await updateProject(updateData);

      if (result) {
        toast.success("Project updated successfully!");
        navigate(isAdmin ? "/admin" : "/dashboard/project-holder");
      } else {
        toast.error("Failed to update project. Please try again.");
      }
    } catch (error: any) {
      console.error("Error updating project:", error);
      toast.error(error.message || "Failed to update project. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Project Not Found</h2>
          <p className="text-muted-foreground mb-6">The project you're trying to edit doesn't exist or has been removed.</p>
          <Button asChild>
            <Link to={isAdmin ? "/admin" : "/dashboard/project-holder"}>Back to Dashboard</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <img src="/logo.png" alt="Knowvest.plus Logo" className="w-6 h-6 object-contain" />
              </div>
              <h1 className="font-bold text-xl">Knowvest.plus</h1>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link to={isAdmin ? "/admin" : "/dashboard/project-holder"}>
                  <Home className="w-4 h-4 mr-2" />
                  Dashboard
                </Link>
              </Button>

              {user ? (
                <Button variant="outline" size="sm" onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              ) : (
                <Button variant="outline" size="sm" asChild>
                  <Link to="/login">Login</Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">تعديل المشروع</h1>
            <p className="text-muted-foreground">
              قم بتحديث معلومات مشروعك وتفاصيل التمويل
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>معلومات المشروع</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <Label htmlFor="title">اسم المشروع *</Label>
                    <Input
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                      placeholder="أدخل اسم المشروع"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="description">وصف المشروع *</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      required
                      placeholder="قدم وصفًا مفصلًا لمشروعك"
                      rows={4}
                    />
                  </div>

                  <div>
                    <Label htmlFor="category">التصنيف *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => handleSelectChange("category", value)}
                    >
                      <SelectTrigger id="category">
                        <SelectValue placeholder="اختر فئة المشروع" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Business">Business / Operating Project</SelectItem>
                        <SelectItem value="Agriculture">Agriculture / Farm</SelectItem>
                        <SelectItem value="Factory">Factory / Industrial</SelectItem>
                        <SelectItem value="Innovation">Innovation / Invention</SelectItem>
                        <SelectItem value="Intellectual Property">Intellectual Property (IP)</SelectItem>
                        <SelectItem value="Prototype">Prototype / R&D Concept</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="location">الموقع</Label>
                    <Input
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="أدخل موقع المشروع"
                    />
                  </div>

                  <div>
                    <Label htmlFor="fundingGoal">الهدف المالي (د.ج) *</Label>
                    <Input
                      id="fundingGoal"
                      name="fundingGoal"
                      type="number"
                      value={formData.fundingGoal}
                      onChange={handleChange}
                      required
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <Label htmlFor="minInvestment">الحد الأدنى للاستثمار (د.ج) *</Label>
                    <Input
                      id="minInvestment"
                      name="minInvestment"
                      type="number"
                      value={formData.minInvestment}
                      onChange={handleChange}
                      required
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <Label htmlFor="expectedROI">عائد الاستثمار المتوقع (%) *</Label>
                    <Input
                      id="expectedROI"
                      name="expectedROI"
                      type="number"
                      value={formData.expectedROI}
                      onChange={handleChange}
                      required
                      placeholder="0"
                    />
                  </div>

                  {/* Only show project purpose for admin users */}
                  {isAdmin && (
                    <div>
                      <Label htmlFor="projectPurpose">{language === "ar" ? t("projectPurpose.title") : t("projectPurpose.title")}</Label>
                      <Select
                        value={formData.projectPurpose}
                        onValueChange={(value) => handleSelectChange("projectPurpose", value)}
                      >
                        <SelectTrigger id="projectPurpose">
                          <SelectValue placeholder={language === "ar" ? t("projectPurpose.selectPurpose") : t("projectPurpose.selectPurpose")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sell">{language === "ar" ? t("projectPurpose.wantToSell") : t("projectPurpose.wantToSell")}</SelectItem>
                          <SelectItem value="getInvestment">{language === "ar" ? t("projectPurpose.wantToGetInvestment") : t("projectPurpose.wantToGetInvestment")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Category-specific fields */}
                {formData.category === 'Innovation' && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="developmentStage">Development Stage</Label>
                      <Select
                        value={formData.developmentStage}
                        onValueChange={(value) => handleSelectChange("developmentStage", value)}
                      >
                        <SelectTrigger id="developmentStage">
                          <SelectValue placeholder="Select development stage" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Idea">Idea / Concept</SelectItem>
                          <SelectItem value="Prototype">Prototype built</SelectItem>
                          <SelectItem value="MVP">MVP tested</SelectItem>
                          <SelectItem value="Patent Filed">Patent filed</SelectItem>
                          <SelectItem value="Market-ready">Market-ready</SelectItem>
                          <SelectItem value="Generating Revenue">Already generating revenue</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="problemStatement">Problem Statement</Label>
                      <Textarea
                        id="problemStatement"
                        name="problemStatement"
                        value={formData.problemStatement}
                        onChange={handleChange}
                        placeholder="What problem exists today?"
                        rows={3}
                      />
                    </div>

                    <div>
                      <Label htmlFor="solutionDescription">Solution Description</Label>
                      <Textarea
                        id="solutionDescription"
                        name="solutionDescription"
                        value={formData.solutionDescription}
                        onChange={handleChange}
                        placeholder="How this invention solves it"
                        rows={3}
                      />
                    </div>

                    <div>
                      <Label htmlFor="technologyDetails">Technology / Design Details</Label>
                      <Textarea
                        id="technologyDetails"
                        name="technologyDetails"
                        value={formData.technologyDetails}
                        onChange={handleChange}
                        placeholder="Technical description, materials, components, etc."
                        rows={3}
                      />
                    </div>

                    <div>
                      <Label htmlFor="ipStatus">Intellectual Property Status</Label>
                      <Select
                        value={formData.ipStatus}
                        onValueChange={(value) => handleSelectChange("ipStatus", value)}
                      >
                        <SelectTrigger id="ipStatus">
                          <SelectValue placeholder="Select IP status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Patent Pending">Patent Pending</SelectItem>
                          <SelectItem value="Patent Filed">Patent Filed</SelectItem>
                          <SelectItem value="Patent Granted">Patent Granted</SelectItem>
                          <SelectItem value="Trademark">Trademark</SelectItem>
                          <SelectItem value="Copyright">Copyright</SelectItem>
                          <SelectItem value="Trade Secret">Trade Secret</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="marketPotential">Market Potential</Label>
                      <Textarea
                        id="marketPotential"
                        name="marketPotential"
                        value={formData.marketPotential}
                        onChange={handleChange}
                        placeholder="Target industry, market size, use cases, competitive advantage"
                        rows={3}
                      />
                    </div>

                    <div>
                      <Label htmlFor="monetizationOptions">Monetization Options</Label>
                      <Textarea
                        id="monetizationOptions"
                        name="monetizationOptions"
                        value={formData.monetizationOptions}
                        onChange={handleChange}
                        placeholder="Sell full rights, License, Partner for production, Royalty-based model"
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Assets Included in the Sale</Label>
                      <div className="space-y-2">
                        {[
                          { id: "designs", label: "Designs/Files" },
                          { id: "prototype", label: "Prototype" },
                          { id: "documentation", label: "Documentation" },
                          { id: "support", label: "Support Period" },
                          { id: "equipment", label: "Equipment" },
                          { id: "intellectualProperty", label: "Intellectual Property" },
                          { id: "other", label: "Other" },
                        ].map((asset) => (
                          <div key={asset.id} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id={`asset-${asset.id}`}
                              checked={formData.assetsIncluded.includes(asset.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData(prev => ({
                                    ...prev,
                                    assetsIncluded: [...prev.assetsIncluded, asset.id]
                                  }));
                                } else {
                                  setFormData(prev => ({
                                    ...prev,
                                    assetsIncluded: prev.assetsIncluded.filter(id => id !== asset.id)
                                  }));
                                }
                              }}
                              className="h-4 w-4"
                            />
                            <Label htmlFor={`asset-${asset.id}`} className="text-sm font-normal cursor-pointer">
                              {asset.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Financial Overview - Admin Only */}
                    {isAdmin && (
                      <div className="space-y-2">
                        <Label htmlFor="financialOverview">Financial Overview (Admin Only)</Label>
                        <Textarea
                          id="financialOverview"
                          name="financialOverview"
                          value={formData.financialOverview}
                          onChange={handleChange}
                          placeholder="Enter financial overview after conducting financial studies"
                          rows={4}
                        />
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <Label>صور المشروع</Label>
                  <div className="mt-2">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-muted/30 hover:bg-muted/50">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <ImageIcon className="w-8 h-8 mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          انقر لتحميل صور المشروع
                        </p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                      />
                    </label>

                    {(images.length > 0 || (project?.images && project.images.length > 0)) && (
                      <div className="mt-4 grid grid-cols-3 gap-2">
                        {/* Display existing project images */}
                        {project?.images && project.images.map((image, index) => (
                          <div key={`existing-${index}`} className="relative aspect-square bg-muted rounded-lg overflow-hidden">
                            <a href={image} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
                              <img
                                src={image}
                                alt={`Project image ${index + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.src = '/placeholder-image.jpg'; // fallback image
                                }}
                              />
                            </a>
                            <button
                              type="button"
                              className="absolute -top-2 -right-2 w-6 h-6 bg-destructive rounded-full flex items-center justify-center text-white"
                              onClick={(e) => {
                                e.preventDefault();
                                deleteExistingImage(index);
                              }}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        {/* Display newly selected images */}
                        {images.map((image, index) => (
                          <div key={`new-${index}`} className="relative">
                            <div className="aspect-square bg-muted rounded-lg flex items-center justify-center">
                              <ImageIcon className="w-6 h-6 text-muted-foreground" />
                            </div>
                            <button
                              type="button"
                              className="absolute -top-2 -right-2 w-6 h-6 bg-destructive rounded-full flex items-center justify-center text-white"
                              onClick={() => removeImage(index)}
                            >
                              <X className="w-4 h-4" />
                            </button>
                            <p className="text-xs text-center truncate mt-1">{image.name}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <Label>مستندات المشروع</Label>
                  <div className="mt-2">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-muted/30 hover:bg-muted/50">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <FileText className="w-8 h-8 mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          انقر لتحميل مستندات المشروع
                        </p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.doc,.docx,.xls,.xlsx"
                        multiple
                        onChange={handleDocumentUpload}
                      />
                    </label>

                    {(documents.length > 0 || (project?.documents && project.documents.length > 0)) && (
                      <div className="mt-4 space-y-2">
                        {/* Display existing project documents */}
                        {project?.documents && project.documents.map((document, index) => (
                          <div key={`existing-doc-${index}`} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-primary" />
                              <span className="text-sm font-medium truncate max-w-[200px]">Existing Document {index + 1}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <a href={document} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm">View</a>
                              <a href={document} download className="text-primary hover:underline text-sm">Download</a>
                              <button
                                type="button"
                                className="w-6 h-6 bg-destructive rounded-full flex items-center justify-center text-white"
                                onClick={(e) => {
                                  e.preventDefault();
                                  deleteExistingDocument(index);
                                }}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                        {/* Display newly selected documents */}
                        {documents.map((document, index) => (
                          <div key={`new-doc-${index}`} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-primary" />
                              <span className="text-sm font-medium truncate max-w-[200px]">{document.name}</span>
                            </div>
                            <button
                              type="button"
                              className="w-6 h-6 bg-destructive rounded-full flex items-center justify-center text-white"
                              onClick={() => removeDocument(index)}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-4 pt-4">
                  <Button variant="outline" type="button" asChild>
                    <Link to={isAdmin ? "/admin" : "/dashboard/project-holder"}>إلغاء</Link>
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? "جاري التحديث..." : "تحديث المشروع"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default EditProject;
